import { TILE_SIZE, Terrain } from "@webolo/shared";

// Procedurally draws a tile atlas into an offscreen canvas. Each terrain
// type gets a distinct, readable look — real WinBolo sprites will drop in
// here later by swapping this function for an image load of bolo_tiles.png.
export function buildTileAtlas(): HTMLCanvasElement {
  const types = [
    Terrain.DeepSea,
    Terrain.BoatOrBuilding,
    Terrain.River,
    Terrain.Swamp,
    Terrain.Crater,
    Terrain.Road,
    Terrain.Forest,
    Terrain.Rubble,
    Terrain.Grass,
    Terrain.Wall,
    Terrain.HalfBuiltWall,
    Terrain.Boat,
    Terrain.Base,
    Terrain.Pillbox,
  ];
  const max = Math.max(...types) + 1;
  const canvas = document.createElement("canvas");
  canvas.width = max * TILE_SIZE;
  canvas.height = TILE_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("tile atlas ctx unavailable");
  for (const t of types) drawTile(ctx, t, t * TILE_SIZE, 0);
  return canvas;
}

function drawTile(ctx: CanvasRenderingContext2D, t: Terrain, x: number, y: number): void {
  const s = TILE_SIZE;
  switch (t) {
    case Terrain.DeepSea:
      fill(ctx, x, y, "#1b3a6b");
      hatch(ctx, x, y, "#244a84");
      break;
    case Terrain.River:
      fill(ctx, x, y, "#2e6ab0");
      hatch(ctx, x, y, "#3d7dc8");
      break;
    case Terrain.Swamp:
      fill(ctx, x, y, "#4a5c2e");
      speckle(ctx, x, y, "#2f3d1f", 18);
      break;
    case Terrain.Crater:
      fill(ctx, x, y, "#6a5a3a");
      speckle(ctx, x, y, "#4a3f28", 12);
      break;
    case Terrain.Road:
      fill(ctx, x, y, "#8a7f5a");
      ctx.fillStyle = "#766b4c";
      ctx.fillRect(x + 2, y, s - 4, s);
      break;
    case Terrain.Forest:
      fill(ctx, x, y, "#2d5a1f");
      tree(ctx, x + 3, y + 3);
      tree(ctx, x + 9, y + 2);
      tree(ctx, x + 5, y + 9);
      tree(ctx, x + 11, y + 10);
      break;
    case Terrain.Rubble:
      fill(ctx, x, y, "#7a6a55");
      speckle(ctx, x, y, "#574a3a", 24);
      break;
    case Terrain.Grass:
      fill(ctx, x, y, "#3e7a2a");
      speckle(ctx, x, y, "#4f9137", 14);
      break;
    case Terrain.Wall:
      fill(ctx, x, y, "#6d6d6d");
      ctx.strokeStyle = "#3d3d3d";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < 2; i++) {
        ctx.moveTo(x, y + 4 + i * 8);
        ctx.lineTo(x + s, y + 4 + i * 8);
      }
      ctx.moveTo(x + 4, y);
      ctx.lineTo(x + 4, y + 4);
      ctx.moveTo(x + 12, y + 4);
      ctx.lineTo(x + 12, y + 12);
      ctx.moveTo(x + 4, y + 12);
      ctx.lineTo(x + 4, y + s);
      ctx.stroke();
      break;
    case Terrain.HalfBuiltWall:
      fill(ctx, x, y, "#8d8d8d");
      ctx.fillStyle = "#5d5d5d";
      ctx.fillRect(x + 1, y + 1, s - 2, s - 2);
      break;
    case Terrain.Boat:
      fill(ctx, x, y, "#2e6ab0");
      ctx.fillStyle = "#8b5a2b";
      ctx.beginPath();
      ctx.moveTo(x + 2, y + 8);
      ctx.lineTo(x + s - 2, y + 8);
      ctx.lineTo(x + s - 4, y + 13);
      ctx.lineTo(x + 4, y + 13);
      ctx.closePath();
      ctx.fill();
      break;
    case Terrain.Base:
      fill(ctx, x, y, "#3a3a3a");
      ctx.fillStyle = "#d6a04b";
      ctx.fillRect(x + 3, y + 3, s - 6, s - 6);
      ctx.fillStyle = "#3a3a3a";
      ctx.fillRect(x + 6, y + 6, s - 12, s - 12);
      break;
    case Terrain.Pillbox:
      fill(ctx, x, y, "#3e7a2a");
      ctx.fillStyle = "#8a3a3a";
      ctx.beginPath();
      ctx.arc(x + s / 2, y + s / 2, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#3a1515";
      ctx.lineWidth = 1;
      ctx.stroke();
      break;
    default:
      fill(ctx, x, y, "#ff00ff");
      break;
  }
}

function fill(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, TILE_SIZE, TILE_SIZE);
}

function hatch(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = -TILE_SIZE; i < TILE_SIZE * 2; i += 4) {
    ctx.moveTo(x + i, y);
    ctx.lineTo(x + i + TILE_SIZE, y + TILE_SIZE);
  }
  ctx.stroke();
}

function speckle(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, count: number): void {
  ctx.fillStyle = color;
  // Deterministic pseudo-random so tiles don't shimmer across redraws.
  let a = (x * 73856093) ^ (y * 19349663);
  for (let i = 0; i < count; i++) {
    a = (a * 1103515245 + 12345) & 0x7fffffff;
    const px = x + (a % TILE_SIZE);
    a = (a * 1103515245 + 12345) & 0x7fffffff;
    const py = y + (a % TILE_SIZE);
    ctx.fillRect(px, py, 1, 1);
  }
}

function tree(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = "#1a3d13";
  ctx.fillRect(x, y, 3, 3);
  ctx.fillStyle = "#245a1a";
  ctx.fillRect(x, y, 2, 2);
}
